
















































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { TipSubjectType } from "@scrile/api-provider/dist/api/TipProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { VaultFileUsageSubjectTypeEnum } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useVaultView from "@/hooks/useVaultView";
import emitter from "@/lib/emitter";
import { isFile } from "@/lib/fileHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile } from "@/types";
import { EVENTS } from "@/const";
import AttachmentFilesPreview from "@/components/AttachmentFilesPreview";
import AttachmentPriceSetting from "@/components/AttachmentPriceSetting";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFile from "@/components/BaseInputFile";
import BaseSendMessage from "@/components/BaseSendMessage";
import TipController from "@/components/TipController";
import TipIcon from "@/components/TipIcon";
import VaultModal from "@/components/VaultModal";

export default defineComponent({
  name: "MessageSendWithAttachmentView",
  components: {
    VaultModal,
    BaseSendMessage,
    BaseIconButton,
    BaseInputFile,
    AttachmentFilesPreview,
    AttachmentPriceSetting,
    TipController,
    TipIcon,
  },
  props: {
    sending: { type: Boolean, required: true },
    packageList: { type: Array as () => PaymentPackage[], required: true },
    files: { type: Array as () => AttachmentFile[], default: () => [] },
    attachmentPriceId: { type: String, required: true },
    recipientIds: { type: Array as () => string[], required: true },
    threadId: { type: String, default: "0" },
    uploadingAttachment: { type: Boolean, required: true },
    isIncomingMessagesAllow: { type: Boolean, default: false },
    priceAmount: { type: String },
    allowedPublicFilesCount: { type: Number, default: 0 },
    allowedPrivateFilesCount: { type: Number, default: 1 },
  },
  setup(props, { emit }) {
    const { authUserStrict, userVerificationStatus } = useAuthUser();
    const value = ref<string>("");
    const hasText = computed(() => value.value.trim().length > 0);
    const hasAttachment = computed(() => props.files && props.files.length > 0);
    const canTip = computed<boolean>(() => config.featureTippingMessages && !hasText.value && !hasAttachment.value);
    const tipSubject = computed(() => ({
      subjectId: props.recipientIds[0],
      subjectType: TipSubjectType.USER,
    }));
    const loadAttachIconColor = computed(() =>
      props.allowedPrivateFilesCount === 0 ? "var(--palette-gray-lighten-6)" : "var(--palette-gray)"
    );

    const checkOnApproved = (id: string) => {
      if (props.files && props.files.length > 0 && userVerificationStatus.value === UserVerificationStatus.VERIFIED) {
        emit("setPrice", id);
        return;
      }
      emit("setPrice", "0");
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: "Price setup declined: your account is pending approval" });
    };

    const allowSend = (value: string) => {
      return !props.sending && !props.uploadingAttachment && (hasAttachment.value || value.length > 0);
    };

    const onSend = (v: string, cb: () => void) => {
      value.value = "";
      emit("send", v, cb);
    };

    const isNoPrice = computed(() => props.attachmentPriceId === "0");

    const isUploadingAvailable = (access: boolean = false) => {
      return (access && props.allowedPublicFilesCount !== 0) || (!access && props.allowedPrivateFilesCount !== 0);
    };
    const { showPrivateVault, showPublicVault, isVaultAvailable, showVaultOrNative } = useVaultView();
    const onAddFiles = (showNativeUploader: () => void, publicAccess: boolean): void => {
      if (isUploadingAvailable(publicAccess)) {
        showVaultOrNative(showNativeUploader, publicAccess);
      }
    };
    const addAttachment = (files: (AttachmentFile | File)[], publicAccess: boolean) => {
      const fileList: (InputAttachmentFile | AttachmentFile)[] = files.map(file => {
        if (isFile(file)) {
          return { file, publicAccess };
        }
        file.publicAccess = publicAccess;
        return file;
      });
      fileList.length > 0 && emit("addAttachment", fileList);
    };

    const threadSubject = computed<Subject<VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD>>(() => {
      return {
        subjectId: props.threadId,
        subjectType: VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD,
      };
    });

    return {
      showPrivateVault,
      showPublicVault,
      value,
      canTip,
      tipSubject,
      isNoPrice,
      userVerificationStatus,
      authUserStrict,
      hasAttachment,
      hasText,
      isVaultAvailable,
      allowSend,
      onSend,
      checkOnApproved,
      addAttachment,
      onAddFiles,
      loadAttachIconColor,
      threadSubject,
    };
  },
});
